import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const UTC_SUFFIX = ' [UTC]';

// Enum for date formats
export enum DateFormat {
  DateTimeDefault = 'YYYY-MM-DD HH:mm',
  ShortMonthDayYear = 'MMM DD YYYY',
  ShortMonthDayYearCommaSeparated = 'MMM DD, YYYY',
  Time24Hour = 'HH:mm',
  Time24HourWithSeconds = 'HH:mm:ss',
}

export interface FormatDateTimeUTCOptions {
  includeSuffix?: boolean;
  commaAfterDay?: boolean;
}

export interface FormatTimeToUTCOptions {
  showSeconds?: boolean;
  showUTCSuffix?: boolean;
}

export interface FormatDurationOptions {
  /** How many non-zero units to keep, largest first. Defaults to 2. */
  maxUnits?: number;
}

// Check if date string is a valid ISO date
export const isValidISODate = (dateString: string): boolean => {
  // Accepts ISO, or 'YYYY-MM-DD HH:mm:ss(.SSSSSS)'
  const isoRegex = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  return isoRegex.test(dateString);
};

// Convert ISO date string to local time
export const convertToLocalTime = (isoDateString: string, dateFormat: DateFormat = DateFormat.DateTimeDefault) => {
  if (!isoDateString) return null;
  // Accept and sanitize 'YYYY-MM-DD HH:mm:ss(.SSSSSS)' to 'YYYY-MM-DD HH:mm:ss'
  let safeString = isoDateString.replace('T', ' ');
  if (safeString.includes('.')) safeString = safeString.split('.')[0] ?? safeString;
  // Remove timezone if present
  safeString = safeString.replace(/(Z|[+-][0-2]\d:[0-5]\d)$/, '');
  if (!isValidISODate(safeString.replace(' ', 'T'))) return null;
  return dayjs(safeString).local().format(dateFormat);
};

// Renders date to display in local time for table columns
export const renderDateColumn = (date: string) => {
  let dateForDisplay: string | null = null;

  if (date) {
    dateForDisplay = convertToLocalTime(date);
  }

  return <span data-testid="date-column">{dateForDisplay}</span>;
};

export const formatDateTimeUTC = (isoString: string | null | undefined, options?: FormatDateTimeUTCOptions): string => {
  if (!isoString) return '';

  const date = dayjs.utc(isoString);

  if (!date.isValid()) return '';

  const { includeSuffix = true, commaAfterDay = true } = options ?? {};

  let formatString;
  if (includeSuffix) {
    formatString = commaAfterDay
      ? `${DateFormat.ShortMonthDayYearCommaSeparated} ${DateFormat.Time24Hour}${UTC_SUFFIX}`
      : `${DateFormat.ShortMonthDayYear}, ${DateFormat.Time24Hour}${UTC_SUFFIX}`;
  } else {
    formatString = commaAfterDay
      ? `${DateFormat.ShortMonthDayYearCommaSeparated} ${DateFormat.Time24Hour}`
      : `${DateFormat.ShortMonthDayYear}, ${DateFormat.Time24Hour}`;
  }

  return date.format(formatString);
};

export const formatTimeToUTC = (isoString: string | null | undefined, options?: FormatTimeToUTCOptions): string => {
  if (!isoString) return '';
  const date = dayjs.utc(isoString);
  if (!date.isValid()) return '';

  const { showSeconds = true, showUTCSuffix = true } = options ?? {};

  const suffix = showUTCSuffix ? UTC_SUFFIX : '';
  const formatString = showSeconds
    ? `${DateFormat.Time24HourWithSeconds}${suffix}`
    : `${DateFormat.Time24Hour}${suffix}`;
  return date.format(formatString);
};

const DURATION_UNITS = [
  { label: 'd', ms: 86_400_000 },
  { label: 'h', ms: 3_600_000 },
  { label: 'm', ms: 60_000 },
  { label: 's', ms: 1000 },
] as const;

export const DURATION_UNIT_COUNT = DURATION_UNITS.length;

/**
 * Formats the elapsed time between two dates using its non-zero units, largest first.
 *
 * By default, only the two largest units are kept (`2s`, `1m 30s`, `2h 5m`, `14d 3h`) so the
 * value stays compact in dense layouts. Pass `maxUnits` to get the full precision
 * (e.g. `14d 3h 59m 56s`), which matters when the exact span is security-relevant.
 *
 * Returns an empty string when either date is missing or invalid.
 */
export const formatDurationBetween = (
  fromIsoString: string | null | undefined,
  toIsoString: string | null | undefined,
  options?: FormatDurationOptions,
): string => {
  if (!fromIsoString || !toIsoString) return '';

  const from = dayjs.utc(fromIsoString);
  const to = dayjs.utc(toIsoString);

  if (!from.isValid() || !to.isValid()) return '';

  const { maxUnits = 2 } = options ?? {};

  let remaining = Math.abs(to.diff(from));

  const parts: string[] = [];
  for (const { label, ms } of DURATION_UNITS) {
    const value = Math.floor(remaining / ms);
    remaining -= value * ms;

    if (value > 0) parts.push(`${value}${label}`);
    if (parts.length === maxUnits) break;
  }

  return parts.length > 0 ? parts.join(' ') : '0s';
};
